import React, { useState } from 'react'
import { AppLayout } from '../../layouts/AppLayout'
import { PageHeader } from '../../layouts/PageHeader'
import { useUserSession } from '../../../context/UserSessionContext'

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP'] as const

export const UserProfilePage: React.FC = () => {
  const {
    googleUser,
    isSignedIn,
    profile,
    setProfile,
    workspace,
    setWorkspace,
    notifications,
    setNotifications,
    logAudit,
    emailLine,
  } = useUserSession()

  const [savedFlash, setSavedFlash] = useState(false)

  const handleSave = () => {
    logAudit('Profile updated', 'Personal & reporting preferences')
    setSavedFlash(true)
    window.setTimeout(() => setSavedFlash(false), 2200)
  }

  return (
    <AppLayout>
      <PageHeader
        title="Profile"
        subtitle="Personal details and default reporting preferences for this browser."
      />
      <div className="p-6 max-w-3xl space-y-6">
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80">
            <h2 className="text-sm font-semibold text-slate-900">Identity</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {isSignedIn
                ? 'Linked Google account — email is read-only.'
                : 'Sign in from the home app to link Google.'}
            </p>
          </div>
          <div className="p-5 space-y-4">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Email
              </span>
              <input
                type="email"
                readOnly
                value={emailLine === 'Not signed in' ? '' : emailLine}
                placeholder="Not signed in"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-slate-50 text-slate-600"
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Display name
              </span>
              <input
                type="text"
                value={profile.preferredName}
                onChange={e => setProfile({ preferredName: e.target.value })}
                placeholder={
                  googleUser?.displayName
                    ? `Default: ${googleUser.displayName}`
                    : 'How your name appears in v2'
                }
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Job title
              </span>
              <input
                type="text"
                value={profile.jobTitle}
                onChange={e => setProfile({ jobTitle: e.target.value })}
                placeholder="e.g. Finance Analyst"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Phone
                </span>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={e => setProfile({ phone: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Timezone
                </span>
                <input
                  type="text"
                  value={profile.timezone}
                  onChange={e => setProfile({ timezone: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80">
            <h2 className="text-sm font-semibold text-slate-900">
              Reporting defaults
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Used as your personal default where charts support a currency
              selector.
            </p>
          </div>
          <div className="p-5 space-y-4">
            <label className="block max-w-xs">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Default currency
              </span>
              <select
                value={workspace.defaultReportingCurrency}
                onChange={e =>
                  setWorkspace({ defaultReportingCurrency: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {CURRENCIES.map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="accent-blue-600 rounded"
                checked={notifications.weeklyDigestEmail}
                onChange={e =>
                  setNotifications({ weeklyDigestEmail: e.target.checked })
                }
              />
              <span className="text-sm text-slate-700">
                Weekly digest email (when backend delivery is enabled)
              </span>
            </label>
          </div>
        </section>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            className="rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700 transition-colors"
          >
            Save changes
          </button>
          {savedFlash && (
            <span className="text-sm text-emerald-600 font-medium">
              Saved locally in this browser.
            </span>
          )}
        </div>
      </div>
    </AppLayout>
  )
}

export default UserProfilePage
